import React, { useState, useEffect, useRef } from "react";
import { Form, Row, Col } from "react-bootstrap";
import {
  financingSources,
  otherDatasets,
  numbersAtAGlance,
  dataTip,
} from "../dropdown-options/dashboard";
import "./dashboard copy.css";

/**
 * CardStatistics Component
 * 
 * Displays key statistics on the dashboard with dynamic dropdown selection.
 * Allows users to choose different statistics categories (funding sources, other stats, numbers overview).
 * Dynamically adjusts dropdown width based on selected option length.
 * Displays primary statistic with label, and secondary stats in a two-column layout if available.
 * 
 * @param {Object} props - Component props
 * @param {string} props.type - Type of statistics to display: 'fundingSources', 'otherStats', or 'numbersOverview'
 * @param {Object} props.object - Object containing all statistics data organized by category
 * @param {string} props.date - Date string to display for when statistics were last updated
 * @param {string} props.usersDate - Date string to display for user-related statistics
 * @returns {JSX.Element} Statistics card with dropdown selector and formatted data display
 */
const CardStatistics = ({ type, object, date, usersDate }) => {
  // State to manage current selection and data for each statistics category
  // Includes name (display label), dataTip (tooltip), and statistics array
  const [data, setData] = useState({
    fundingSources: {
      name: "Very-early stage funding",
      dataTip: dataTip["veryEarlyStageFunding"],
      statistics: object["veryEarlyStageFunding"],
    },
    otherStats: {
      name: "Pitch decks showcase",
      dataTip: dataTip["pitchDecksShowcase"],
      statistics: object["pitchDecksShowcase"],
    },
    numbersOverview: {
      name: "Total users",
      dataTip: dataTip["totalUsers"],
      statistics: object["totalUsers"],
    },
  });

  const dropdownRef = useRef(null);
  const measureSpanRef = useRef(null);

  // Get the currently selected data based on the 'type' prop.
  const selectedData = data[type];
  const selectedOptionName = selectedData?.name;

  /**
   * Get the correct list of options for the current statistics category
   * Returns appropriate options array based on the type prop
   * 
   * @returns {Array} Array of option objects with key and value properties
   */
  const getOptions = () => {
    switch (type) {
      case "fundingSources":
        return financingSources;
      case "otherStats":
        return otherDatasets;
      case "numbersOverview":
        return numbersAtAGlance;
      default:
        return [];
    }
  };

  /**
   * Handle when user selects a new statistic option from the dropdown
   * Updates state with new selection, name, tooltip, and corresponding statistics data
   * 
   * @param {Object} e - Event object from dropdown select
   */
  function selectStatistic(e) {
    e.preventDefault();

    const chosenOptionValue = e.target.value;
    const chosenOption = getOptions().find(option => option.value === chosenOptionValue);

    // Update the state with the new selection and its data
    setData({
      ...data,
      [type]: {
        name: chosenOption.key,
        dataTip: dataTip[chosenOptionValue],
        statistics: object[chosenOptionValue],
      },
    });
  }

  // Effect to dynamically adjust the dropdown width based on selected option name
  // Measures the text width of the selected option and adds padding
  useEffect(() => {
    if (dropdownRef.current && measureSpanRef.current && selectedOptionName) {
      // Set the hidden span content to match the selected option name
      measureSpanRef.current.textContent = selectedOptionName;

      // Calculate width based on span width plus padding for visual spacing
      const width = measureSpanRef.current.offsetWidth + 70;

      // Apply the calculated width to the dropdown
      dropdownRef.current.style.width = `${width}px`;
    }
  }, [selectedOptionName, type]);

  return (
    <>
      <p className="dashboardCardHeader">
        <Form.Select onChange={selectStatistic} ref={dropdownRef}>
          {getOptions().map((option, index) => (
            <option
              name={option.key}
              value={option.value}
              key={index}
              // Set the 'selected' attribute to show the correct option.
              selected={selectedOptionName === option.key}
            >
              {option.key}
            </option>
          ))}
        </Form.Select>

        {/* This hidden span is used to measure the text's width. */}
        <span
          ref={measureSpanRef}
          style={{
            visibility: "hidden",
            whiteSpace: "nowrap",
            position: "absolute",
            fontFamily: "inherit",
            fontSize: "inherit",
            fontWeight: "inherit",
          }}
        />

        {/* <ReactTooltip
          className="navbartooltip"
          place="bottom"
          type="dark"
          effect="solid"
        />

        {selectedData.dataTip !== "" ? (
          <sup data-tip={selectedData.dataTip}>
            <IoMdInformationCircleOutline id="information-icon" />
          </sup>
        ) : (
          ""
        )} */}
      </p>

      <hr />

      <p className="dashboardCardData">
        {selectedData.statistics[0].stats
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
      </p>

      <p className="dashboardCardLabel">{selectedData.statistics[0].label}</p>

      {selectedData.statistics.length > 1 ? (
        <>
          <br />
          <Row xs={2} sm={2} className="dashboardCardSub">
            <Col>
              <p className="dashboardCardSubLabel">
                {selectedData.statistics[1].label}
              </p>
            </Col>
            <Col>
              <p className="dashboardCardSubData">
                {selectedData.statistics[1].stats
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              </p>
            </Col>
          </Row>
        </>
      ) : (
        ""
      )}

      <div className="dashboardBottomDiv">
        <br />

        <p className="dashboardMinorText">Updated: {selectedData.statistics[0].label === "Users" ? usersDate : date}</p>
      </div>
    </>
  );
};

export default CardStatistics;
